import moment from 'moment'
import type { IChatMessage } from 'features/Chat/Chat.types'

/**
 * Reshapes a flat message list into the day sections and same-sender runs that
 * iOS Messages renders.
 *
 * Kept pure and separate from the components so the bucketing rules can be
 * tested without mounting a thread.
 */

export interface IMessageRun {
	senderId: string
	/** True when the run's author is the signed-in user. */
	isOutgoing: boolean
	messages: IChatMessage[]
}

export interface IMessageDay {
	/** Stable key: the day's ISO date. */
	key: string
	/** Today / Yesterday / Monday / Mar 4, 2026 */
	label: string
	/** Clock time of the day's first message, shown beside the label. */
	firstTime: string
	runs: IMessageRun[]
}

/** Consecutive messages from one sender inside this gap form a single run. */
const RUN_GAP_MS = 60 * 1000

export const dayLabel = (dateTime: string | Date) => {
	const now = moment()
	const then = moment(dateTime)

	if (then.isSame(now, 'day')) return 'Today'
	if (then.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday'
	if (then.isAfter(now.clone().subtract(7, 'days').startOf('day'))) return then.format('dddd')
	if (then.isSame(now, 'year')) return then.format('MMM D')
	return then.format('MMM D, YYYY')
}

export interface IMessageDayFlat {
	/** Stable key: the day's ISO date. */
	key: string
	/** "Monday, August 3rd", with the year added when it is not this one. */
	label: string
	messages: IChatMessage[]
}

/**
 * The room chats (project, portfolio, location) show every message in sequence
 * with its author and time, so they need days but not same-sender runs.
 *
 * The label is spelled out in full rather than reusing `dayLabel`: these
 * conversations are read as a record, where "Yesterday" is worse than a date,
 * and the divider doubles as the jump-to control.
 */
export const groupMessagesByDay = (messages: IChatMessage[]): IMessageDayFlat[] => {
	const days: IMessageDayFlat[] = []
	const thisYear = moment().year()

	;(messages || []).forEach(message => {
		const at = moment(message.createdAt)
		const key = at.format('YYYY-MM-DD')
		const last = days[days.length - 1]

		if (last && last.key === key) {
			last.messages.push(message)
			return
		}

		days.push({
			key,
			label:
				at.year() === thisYear
					? at.format('dddd, MMMM Do')
					: at.format('dddd, MMMM Do, YYYY'),
			messages: [message],
		})
	})

	return days
}

export const groupMessages = (messages: IChatMessage[], userId: string): IMessageDay[] => {
	const days: IMessageDay[] = []

	;(messages || []).forEach(message => {
		const at = moment(message.createdAt)
		const key = at.format('YYYY-MM-DD')
		let day = days[days.length - 1]

		if (!day || day.key !== key) {
			day = { key, label: dayLabel(message.createdAt), firstTime: at.format('LT'), runs: [] }
			days.push(day)
		}

		const senderId = message.senderId?._id || ''
		const run = day.runs[day.runs.length - 1]
		const previous = run?.messages[run.messages.length - 1]
		const withinGap =
			previous && at.valueOf() - moment(previous.createdAt).valueOf() <= RUN_GAP_MS

		if (run && run.senderId === senderId && withinGap) {
			run.messages.push(message)
		} else {
			day.runs.push({
				senderId,
				isOutgoing: !!userId && senderId === userId,
				messages: [message],
			})
		}
	})

	return days
}

export default groupMessages
